import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError';
import { expValEquals } from '@confluence/feature-experiments';

import {
	getDraftContentAppearanceProperty,
	getPublishedContentAppearanceProperty,
} from './ContentAppearanceQuery.graphql';
import type { getPublishedContentAppearanceProperty as getPublishedContentAppearancePropertyType } from './__types__/getPublishedContentAppearanceProperty';
import type { getDraftContentAppearanceProperty as getDraftContentAppearancePropertyType } from './__types__/getDraftContentAppearanceProperty';
import type {
	ContentAppearance,
	GraphqlAppearanceContentProperty,
	GraphqlQueryContentWithDraftAppearance,
	GraphqlQueryContentWithPublishedAppearance,
} from './contentAppearanceTypes';
import { ContentAppearancePropertyKey, ContentAppearanceType } from './contentAppearanceTypes';

export const getContentAppearanceFromContentProperties = (
	properties: GraphqlAppearanceContentProperty['nodes'],
	key: ContentAppearancePropertyKey,
): ContentAppearance => {
	/**
	 * We have a case where auto-converted pages have a 'max' appearance without a version.
	 * In this case, we want to return the 'max' appearance even though the version is missing.
	 * When editor_tinymce_full_width_mode is cleaned up, remove this function and just use it to replace the find below.
	 */
	const getContentAppearanceValue = (properties) => {
		return properties?.find((property) => {
			if (!property.version) {
				let propertyValue;
				try {
					propertyValue = JSON.parse(property.value);
					if (propertyValue === ContentAppearanceType.MAX) {
						return true;
					}
				} catch {
					return false;
				}
			} else if (property?.key === key && property?.version) {
				return true;
			}
		});
	};
	const contentAppearanceProperty = expValEquals(
		'editor_tinymce_full_width_mode',
		'isEnabled',
		true,
	)
		? getContentAppearanceValue(properties)
		: properties?.find((property) => property?.key === key && property?.version);

	let appearance = ContentAppearanceType.DEFAULT;
	let version = 0;
	if (contentAppearanceProperty) {
		appearance = contentAppearanceProperty.value
			? JSON.parse(contentAppearanceProperty.value)
			: appearance;
		version = contentAppearanceProperty.version?.number || version;
	}

	return { appearance, version };
};

export const getContentAppearanceDraft = (content: GraphqlQueryContentWithDraftAppearance) => {
	return getContentAppearanceFromContentProperties(
		content.appearanceDraft?.nodes ?? null,
		ContentAppearancePropertyKey.DRAFT,
	);
};

export const getContentAppearancePublished = (
	content: GraphqlQueryContentWithPublishedAppearance,
) => {
	return getContentAppearanceFromContentProperties(
		content.appearancePublished?.nodes ?? null,
		ContentAppearancePropertyKey.PUBLISHED,
	);
};

// if the apollo client is imported in this file instead of passed in as an argument,
// it causes an obscure webpack build error
export const updateContentAppearancePropertyWithRetry = async (
	updateContentAppearanceProperty,
	params,
	apolloClient,
	collabEditProvider?,
) => {
	try {
		await updateContentAppearanceProperty(params);
		updateAppearanceForCollab(collabEditProvider, params);
		return params;
	} catch (error) {
		// If it was an HTTP 409 conflict, retry once with the latest version number + 1
		if (isStatusCodeError(error, '409')) {
			const appearanceProperty = await fetchContentAppearanceProperty(
				params.contentId,
				params.propertyKey,
				apolloClient,
				'network-only',
			);
			if (appearanceProperty.version) {
				params = prepareNewParams(params, appearanceProperty);
				await updateContentAppearanceProperty(params);
				updateAppearanceForCollab(collabEditProvider, params);
				return params;
			}
		}
		throw error;
	}
};

export const prepareNewParams = (params, appearanceProperty) => {
	params.versionNumber = appearanceProperty.version + 1;
	if (params.propertyValue !== appearanceProperty.appearance) {
		params.propertyValue =
			appearanceProperty.appearance === ContentAppearanceType.DEFAULT
				? ContentAppearanceType.FULL_WIDTH
				: ContentAppearanceType.DEFAULT;
	}
	return params;
};

export function getContentAppearanceQuery(propertyKey: ContentAppearancePropertyKey) {
	return propertyKey === ContentAppearancePropertyKey.PUBLISHED
		? getPublishedContentAppearanceProperty
		: getDraftContentAppearanceProperty;
}

export function getContentAppearanceFromQueryData(
	data: getPublishedContentAppearancePropertyType | getDraftContentAppearancePropertyType,
	propertyKey: ContentAppearancePropertyKey,
): ContentAppearance {
	const content = data?.content?.nodes?.[0];

	if (content) {
		return propertyKey === ContentAppearancePropertyKey.PUBLISHED
			? getContentAppearancePublished(content as GraphqlQueryContentWithPublishedAppearance)
			: getContentAppearanceDraft(content as GraphqlQueryContentWithDraftAppearance);
	}
	return getDefaultContentAppearance();
}

export const fetchContentAppearanceProperty = async (
	contentId,
	propertyKey,
	apolloClient,
	fetchPolicy = 'cache-first',
): Promise<ContentAppearance> => {
	const query = getContentAppearanceQuery(propertyKey);

	// This is dummy variable is used to force "network-only" requests to only be run as network calls.
	// A bug in Apollo sometimes makes this query return data from the cache even if the policy is "network-only"
	// See for more details: https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
	const dummy = fetchPolicy === 'network-only' ? Date.now() : undefined;

	const contentAppearanceDraftProperty = await apolloClient.query({
		query,
		variables: {
			contentId,
			status: ['current', 'draft'],
			dummy,
		},
		fetchPolicy,
		context: { single: 'true' },
	});

	return getContentAppearanceFromQueryData(contentAppearanceDraftProperty?.data, propertyKey);
};

export const getDefaultContentAppearance = (): ContentAppearance => {
	return { appearance: ContentAppearanceType.DEFAULT, version: 0 };
};

export const UPDATE_APPEARANCE_VIA_COLLAB_PROVIDER_KEY = 'editorAppearance';

export const updateAppearanceForCollab = (collabEditProvider, params) => {
	if (!collabEditProvider) {
		return;
	}
	// Implies NCS provider
	if (typeof collabEditProvider.setMetadata === 'function') {
		collabEditProvider.setMetadata({
			...collabEditProvider.getMetadata(),
			metadataSource: 'client',
			editorWidth: {
				value: params.propertyValue,
				version: params.versionNumber,
			},
		});
		// Otherwise, Synchrony provider
	} else {
		collabEditProvider.sendMessage({
			type: UPDATE_APPEARANCE_VIA_COLLAB_PROVIDER_KEY,
			appearance: params.propertyValue,
			version: params.versionNumber,
		});
	}
};

export const updateContentAppearancePropertyAfterOfflineMode = async (
	appearanceProperty,
	params,
	updateContentAppearanceProperty,
	collabEditProvider,
) => {
	const newParams = {
		...params,
		propertyValue: appearanceProperty.appearance,
		versionNumber: appearanceProperty.version + 1,
	};

	if (appearanceProperty.version) {
		await updateContentAppearanceProperty(newParams);
		updateAppearanceForCollab(collabEditProvider, newParams);
	}
};
